import React, { useState, useEffect } from 'react';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { useAuth } from '../contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ComposedChart, Area } from 'recharts';
import { TrendingUp, TrendingDown, Award, AlertTriangle, Users, Target, DollarSign, Crown } from 'lucide-react';
import { motion } from 'framer-motion';
import { format, subMonths } from 'date-fns';

const ExecutiveReporting = () => {
  const { analyticsData, predictiveData, loading } = useAnalytics();
  const { user } = useAuth();
  const [selectedTimeframe, setSelectedTimeframe] = useState('quarterly');
  const [selectedView, setSelectedView] = useState('overview');

  // Simulated executive-level data
  const [executiveData, setExecutiveData] = useState({
    organizationalHealth: 87,
    strategicAlignment: 92,
    employeeEngagement: 78,
    operationalEfficiency: 85,
    riskLevel: 'low',
    revenue_impact: 2.4, // in millions
    cost_savings: 1.8 // in millions
  });

  const [departmentPerformance, setDepartmentPerformance] = useState([
    { department: 'Engineering', performance: 88, goals: 92, okrs: 85, reviews: 95, headcount: 45, budget_utilization: 87 },
    { department: 'Sales', performance: 91, goals: 89, okrs: 93, reviews: 88, headcount: 32, budget_utilization: 95 },
    { department: 'Marketing', performance: 82, goals: 78, okrs: 80, reviews: 90, headcount: 18, budget_utilization: 73 },
    { department: 'HR', performance: 85, goals: 90, okrs: 82, reviews: 98, headcount: 12, budget_utilization: 68 },
    { department: 'Finance', performance: 94, goals: 96, okrs: 91, reviews: 92, headcount: 15, budget_utilization: 89 }
  ]);

  const [kpiTrends, setKpiTrends] = useState([
    { month: 'Jan', performance: 82, engagement: 75, alignment: 88, efficiency: 80 },
    { month: 'Feb', performance: 85, engagement: 78, alignment: 90, efficiency: 83 },
    { month: 'Mar', performance: 87, engagement: 82, alignment: 91, efficiency: 85 },
    { month: 'Apr', performance: 89, engagement: 85, alignment: 93, efficiency: 87 },
    { month: 'May', performance: 91, engagement: 87, alignment: 94, efficiency: 89 },
    { month: 'Jun', performance: 88, engagement: 84, alignment: 92, efficiency: 86 }
  ]);

  const strategicInitiatives = [
    {
      initiative: 'Digital Transformation',
      progress: 78,
      budget: 2.5,
      timeline: 'Q4 2024',
      risk: 'medium',
      impact: 'high',
      owner: 'CTO'
    },
    {
      initiative: 'Employee Development',
      progress: 92,
      budget: 1.2,
      timeline: 'Q3 2024',
      risk: 'low',
      impact: 'high',
      owner: 'CHRO'
    },
    {
      initiative: 'Market Expansion',
      progress: 65,
      budget: 3.8,
      timeline: 'Q1 2025',
      risk: 'high',
      impact: 'high',
      owner: 'CMO'
    },
    {
      initiative: 'Process Optimization',
      progress: 85,
      budget: 0.8,
      timeline: 'Q3 2024',
      risk: 'low',
      impact: 'medium',
      owner: 'COO'
    }
  ];

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'text-purple-600 bg-purple-100';
      case 'medium': return 'text-blue-600 bg-blue-100';
      case 'low': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 90) return 'bg-green-500';
    if (progress >= 70) return 'bg-blue-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Crown className="mr-3 text-yellow-600" size={28} />
            Executive Dashboard
          </h2>
          <p className="text-gray-600 mt-1">Strategic insights and organizational performance overview</p>
        </div>
        
        <div className="flex space-x-4">
          <select
            value={selectedView}
            onChange={(e) => setSelectedView(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            <option value="overview">Performance Overview</option>
            <option value="strategic">Strategic Initiatives</option>
            <option value="departments">Department Analysis</option>
            <option value="risks">Risk Assessment</option>
          </select>
          
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      </div>

      {/* Executive KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Organizational Health</p>
              <p className="text-3xl font-bold">{executiveData.organizationalHealth}%</p>
              <div className="flex items-center mt-2">
                <TrendingUp size={16} className="text-blue-200 mr-1" />
                <span className="text-blue-200 text-sm">+5% from last quarter</span>
              </div>
            </div>
            <Users className="text-blue-200" size={32} />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Strategic Alignment</p>
              <p className="text-3xl font-bold">{executiveData.strategicAlignment}%</p>
              <div className="flex items-center mt-2">
                <TrendingUp size={16} className="text-green-200 mr-1" />
                <span className="text-green-200 text-sm">+8% from last quarter</span>
              </div>
            </div>
            <Target className="text-green-200" size={32} />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Revenue Impact</p>
              <p className="text-3xl font-bold">${executiveData.revenue_impact}M</p>
              <div className="flex items-center mt-2">
                <TrendingUp size={16} className="text-purple-200 mr-1" />
                <span className="text-purple-200 text-sm">Performance driven</span>
              </div>
            </div>
            <DollarSign className="text-purple-200" size={32} />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-lg p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm font-medium">Operational Efficiency</p>
              <p className="text-3xl font-bold">{executiveData.operationalEfficiency}%</p>
              <div className="flex items-center mt-2">
                <TrendingUp size={16} className="text-yellow-200 mr-1" />
                <span className="text-yellow-200 text-sm">+3% improvement</span>
              </div>
            </div>
            <Award className="text-yellow-200" size={32} />
          </div>
        </motion.div>
      </div>

      {selectedView === 'overview' && (
        <>
          {/* Performance Trends */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-lg shadow p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Organizational Performance Trends</h3>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={kpiTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="performance" fill="#3B82F6" fillOpacity={0.3} stroke="#3B82F6" name="Overall Performance" />
                <Line type="monotone" dataKey="engagement" stroke="#10B981" strokeWidth={3} name="Employee Engagement" />
                <Line type="monotone" dataKey="alignment" stroke="#8B5CF6" strokeWidth={3} name="Strategic Alignment" />
                <Bar dataKey="efficiency" fill="#F59E0B" name="Operational Efficiency" />
              </ComposedChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Department Performance Matrix */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-lg shadow p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Performance Matrix</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overall Performance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Goals</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OKRs</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reviews</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Headcount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budget Utilization</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {departmentPerformance.map((dept, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{dept.department}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-full bg-gray-200 rounded-full h-2 mr-3">
                            <div
                              className={`h-2 rounded-full ${getProgressColor(dept.performance)}`}
                              style={{ width: `${dept.performance}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{dept.performance}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{dept.goals}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{dept.okrs}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{dept.reviews}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{dept.headcount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{dept.budget_utilization}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </>
      )}

      {selectedView === 'strategic' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Strategic Initiatives Dashboard</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {strategicInitiatives.map((initiative, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{initiative.initiative}</h4>
                    <p className="text-sm text-gray-600">Owner: {initiative.owner}</p>
                  </div>
                  <div className="flex space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(initiative.risk)}`}>
                      {initiative.risk.toUpperCase()} RISK
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getImpactColor(initiative.impact)}`}>
                      {initiative.impact.toUpperCase()} IMPACT
                    </span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700">Progress</span>
                      <span className="text-sm font-medium text-gray-900">{initiative.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getProgressColor(initiative.progress)}`}
                        style={{ width: `${initiative.progress}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Budget: <span className="font-medium">${initiative.budget}M</span></span>
                    <span className="text-gray-600">Timeline: <span className="font-medium">{initiative.timeline}</span></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {selectedView === 'departments' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Performance Analysis</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Performance Radar Chart */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Performance Radar</h4>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={departmentPerformance.slice(0, 5)}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="department" />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} />
                  <Radar name="Performance" dataKey="performance" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} />
                  <Radar name="Goals" dataKey="goals" stroke="#10B981" fill="#10B981" fillOpacity={0.3} />
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Department Comparison */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Cross-Department Comparison</h4>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={departmentPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="department" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="performance" fill="#3B82F6" name="Performance" />
                  <Bar dataKey="budget_utilization" fill="#10B981" name="Budget Utilization" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>
      )}

      {selectedView === 'risks' && predictiveData.riskAssessment && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="flex items-center mb-6">
            <AlertTriangle className="text-orange-500 mr-2" size={24} />
            <h3 className="text-lg font-semibold text-gray-900">Enterprise Risk Assessment</h3>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Risk Overview */}
            <div className="lg:col-span-2">
              <h4 className="text-md font-medium text-gray-900 mb-4">Identified Risks</h4>
              <div className="space-y-4">
                {predictiveData.riskAssessment.map((risk, index) => (
                  <div key={index} className={`border-l-4 p-4 rounded-r-lg ${
                    risk.severity === 'high' ? 'border-red-500 bg-red-50' :
                    risk.severity === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                    'border-green-500 bg-green-50'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className={`text-xs font-semibold px-2 py-1 rounded uppercase ${
                            risk.severity === 'high' ? 'bg-red-200 text-red-800' :
                            risk.severity === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                            'bg-green-200 text-green-800'
                          }`}>
                            {risk.severity} Risk
                          </span>
                        </div>
                        <h5 className="font-medium text-gray-900 mb-1">{risk.description}</h5>
                        <p className="text-sm text-gray-700 mb-2">{risk.impact}</p>
                        <div className="text-sm">
                          <span className="font-medium text-gray-900">Mitigation: </span>
                          <span className="text-gray-700">{risk.recommendation}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Risk Distribution */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Risk Distribution</h4>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'High Risk', value: predictiveData.riskAssessment.filter(r => r.severity === 'high').length, color: '#EF4444' },
                      { name: 'Medium Risk', value: predictiveData.riskAssessment.filter(r => r.severity === 'medium').length, color: '#F59E0B' },
                      { name: 'Low Risk', value: predictiveData.riskAssessment.filter(r => r.severity === 'low').length, color: '#10B981' }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {predictiveData.riskAssessment.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={
                        entry.severity === 'high' ? '#EF4444' :
                        entry.severity === 'medium' ? '#F59E0B' : '#10B981'
                      } />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              
              {/* Risk Mitigation Actions */}
              <div className="mt-6">
                <h5 className="font-medium text-gray-900 mb-3">Immediate Actions Required</h5>
                <div className="space-y-2">
                  {predictiveData.riskAssessment
                    .filter(risk => risk.severity === 'high')
                    .slice(0, 3)
                    .map((risk, index) => (
                      <div key={index} className="flex items-center p-2 bg-red-50 rounded-lg">
                        <AlertTriangle className="text-red-500 mr-2" size={16} />
                        <span className="text-sm text-red-800">{risk.type.replace('_', ' ')}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ExecutiveReporting;